/* @jsx h */
import { defineComponent, h, ref, computed, watch } from "vue";
import { RightSheet, SegmentSelect } from "@lemmonhq/uikit";
import { isUnsaved, patchDocument } from "../document";
import {
  KIND_META,
  PLATFORM_META,
  WALLET_PLATFORMS,
  type PreviewTarget,
  type WalletPassDesign,
  type WalletPassDesignDocument,
  type WalletPlatform,
} from "../types";
import { WalletPassPreview } from "./WalletPassPreview";
import { DesignChecks, designChecks } from "./DesignChecks";
import {
  ApplePanel,
  AssetsPanel,
  BrowserPanel,
  FieldsPanel,
  GooglePanel,
  StampsPanel,
  StylePanel,
} from "./panels";

type EditorTab =
  | "brand"
  | "images"
  | "content"
  | "apple"
  | "google"
  | "browser"
  | "stamps";

interface WalletDesignEditorProps {
  design: WalletPassDesign | null;
  saving?: boolean;
  scope?: "shop" | "group";
  onClose: () => void;
  onSave: (design: WalletPassDesign) => void;
  /** Emails a test pass of the saved design; resolves to the recipient. */
  onSendTest?: (design: WalletPassDesign) => Promise<string>;
}

export const WalletDesignEditor = defineComponent<WalletDesignEditorProps>({
  name: "WalletDesignEditor",
  props: ["design", "saving", "scope", "onClose", "onSave", "onSendTest"],
  setup(props) {
    const draft = ref<WalletPassDesign | null>(null);
    const tab = ref<EditorTab>("brand");
    const previewTarget = ref<PreviewTarget>("legacy");
    // Which platform the preview rail shows. One design targets several
    // platforms; this only changes what's previewed, not what's saved.
    const previewPlatform = ref<WalletPlatform>("apple");

    /**
     * Re-seed the draft whenever a different design is opened. Keyed on object
     * identity: unsaved drafts all have an empty id.
     */
    watch(
      () => props.design,
      () => {
        if (!props.design) return;
        // A JSON copy: props.design is a reactive proxy, which structuredClone rejects.
        draft.value = JSON.parse(JSON.stringify(props.design));
        tab.value = "brand";
        previewTarget.value = props.design.document.apple.poster.enabled
          ? "poster"
          : "legacy";
        previewPlatform.value = "apple";
        testState.value = null;
      },
      { immediate: true },
    );

    const patch = (p: Partial<WalletPassDesign>) => {
      if (draft.value) draft.value = { ...draft.value, ...p };
    };
    const patchDoc = (p: Partial<WalletPassDesignDocument>) => {
      if (draft.value) draft.value = patchDocument(draft.value, p);
    };
    const replaceDoc = (document: WalletPassDesignDocument) => {
      if (draft.value) draft.value = { ...draft.value, document };
    };
    const togglePlatform = (platform: WalletPlatform, enabled: boolean) => {
      if (!draft.value) return;
      patchDoc({
        [platform]: { ...draft.value.document[platform], enabled },
      } as Partial<WalletPassDesignDocument>);
    };

    const tabs = computed(() => {
      const base: { value: EditorTab; label: string }[] = [
        { value: "brand", label: "Brand" },
        { value: "images", label: "Images" },
        { value: "content", label: "Content" },
        { value: "apple", label: "Apple" },
        { value: "google", label: "Google" },
        { value: "browser", label: "Browser" },
      ];
      if (draft.value?.document.stamps) {
        base.push({ value: "stamps", label: "Stamps" });
      }
      return base;
    });

    const testState = ref<
      { sending: true } | { sent: string } | { error: string } | null
    >(null);
    /** A test pass renders the saved design, so it needs no pending edits. */
    const testable = computed(
      () =>
        !!draft.value &&
        !!props.design &&
        !isUnsaved(draft.value) &&
        (draft.value.kind === "membership" || draft.value.kind === "coin") &&
        JSON.stringify(draft.value.document) ===
          JSON.stringify(props.design.document),
    );
    const sendTest = async () => {
      if (!draft.value || !props.onSendTest) return;
      testState.value = { sending: true };
      try {
        testState.value = { sent: await props.onSendTest(draft.value) };
      } catch (e) {
        testState.value = {
          error: e instanceof Error ? e.message : String(e),
        };
      }
    };

    const checks = computed(() =>
      draft.value ? designChecks(draft.value) : { errors: [], warnings: [] },
    );

    return () => {
      const design = draft.value;

      // RightSheet hardcodes isOpen on its narrow-viewport branch, so a closed
      // sheet would still render full-screen on mobile. Render nothing instead.
      if (!props.design || !design) return null;

      return (
        <RightSheet
          isOpen={true}
          title={design.name}
          onClose={props.onClose}
          width="min(1040px, 100vw)"
          mobileVariant="full-screen"
        >
          <div class="flex flex-col h-full">
            <div class="flex-1 flex flex-col lg:flex-row overflow-hidden">
              {/* Preview rail */}
              <div class="lg:w-[360px] shrink-0 border-b lg:border-b-0 lg:border-r border-base-content/10 bg-base-200/40 overflow-y-auto">
                <div class="p-5 flex flex-col items-center gap-4">
                  <SegmentSelect
                    size="sm"
                    options={WALLET_PLATFORMS.map((p) => ({
                      value: p,
                      label: PLATFORM_META[p].label,
                    }))}
                    value={previewPlatform.value}
                    onChange={(v) =>
                      (previewPlatform.value = (v as WalletPlatform) ?? "apple")
                    }
                  />

                  {previewPlatform.value === "apple" &&
                    design.document.apple.poster.enabled && (
                      <SegmentSelect
                        size="sm"
                        options={[
                          { value: "legacy", label: "iOS 26" },
                          { value: "poster", label: "iOS 27" },
                        ]}
                        value={previewTarget.value}
                        onChange={(v) =>
                          (previewTarget.value =
                            (v as PreviewTarget) ?? "legacy")
                        }
                      />
                    )}

                  {!design.document[previewPlatform.value].enabled && (
                    <div class="alert alert-warning text-xs py-2">
                      <span>
                        {PLATFORM_META[previewPlatform.value].label} is turned
                        off for this design — preview only.
                      </span>
                    </div>
                  )}

                  <WalletPassPreview
                    design={design}
                    platform={previewPlatform.value}
                    target={previewTarget.value}
                  />

                  {props.onSendTest && (
                    <button
                      class="btn btn-sm btn-outline w-full"
                      disabled={
                        !testable.value ||
                        (testState.value !== null &&
                          "sending" in testState.value)
                      }
                      title={
                        testable.value
                          ? "Email yourself this design as a real pass"
                          : "Save the design first (membership and coin designs only)"
                      }
                      onClick={sendTest}
                    >
                      {testState.value && "sending" in testState.value ? (
                        <span class="loading loading-spinner loading-xs" />
                      ) : (
                        <span class="material-symbols-outlined text-sm">
                          send_to_mobile
                        </span>
                      )}
                      Send test pass
                    </button>
                  )}
                  {testState.value && "sent" in testState.value && (
                    <p class="text-[11px] text-success text-center">
                      Sent to {testState.value.sent}. Open it on a phone.
                    </p>
                  )}
                  {testState.value && "error" in testState.value && (
                    <p class="text-[11px] text-error text-center">
                      {testState.value.error}
                    </p>
                  )}

                  <p class="text-[11px] text-base-content/50 text-center">
                    Drawn from the same resolved design as production, with
                    sample data. Native Wallet chrome is approximated.
                  </p>
                </div>
              </div>

              {/* Panels */}
              <div class="flex-1 flex flex-col overflow-hidden">
                <div class="px-4 pt-4 pb-3 border-b border-base-content/10 flex flex-col gap-3">
                  <div class="flex items-center gap-2 flex-wrap">
                    <input
                      class="input input-bordered input-sm flex-1 min-w-[180px] font-medium"
                      value={design.name}
                      onInput={(e) =>
                        patch({ name: (e.target as HTMLInputElement).value })
                      }
                    />
                    <span class="badge badge-ghost gap-1">
                      <span class="material-symbols-outlined text-sm">
                        {KIND_META[design.kind].icon}
                      </span>
                      {KIND_META[design.kind].label}
                    </span>
                    <span class="badge badge-ghost font-mono text-[11px]">
                      {isUnsaved(design) ? "unsaved" : `rev ${design.revision}`}
                    </span>
                  </div>

                  <div class="tabs tabs-boxed bg-base-200/60 w-fit flex-wrap">
                    {tabs.value.map((t) => (
                      <button
                        key={t.value}
                        class={`tab tab-sm ${tab.value === t.value ? "tab-active" : ""}`}
                        onClick={() => (tab.value = t.value)}
                      >
                        {t.label}
                      </button>
                    ))}
                  </div>
                </div>

                <div class="flex-1 overflow-y-auto p-4">
                  {tab.value === "brand" && (
                    <StylePanel design={design} onChange={patchDoc} />
                  )}
                  {tab.value === "images" && (
                    <AssetsPanel
                      design={design}
                      scope={props.scope}
                      onChange={patchDoc}
                      onAssetUrl={(assetId, url) =>
                        patch({
                          asset_urls: { ...design.asset_urls, [assetId]: url },
                        })
                      }
                    />
                  )}
                  {tab.value === "content" && (
                    <FieldsPanel
                      design={design}
                      onChange={patchDoc}
                      onReplace={replaceDoc}
                    />
                  )}
                  {tab.value === "apple" && (
                    <ApplePanel design={design} onChange={patchDoc} />
                  )}
                  {tab.value === "google" && (
                    <GooglePanel design={design} onChange={patchDoc} />
                  )}
                  {tab.value === "browser" && (
                    <BrowserPanel design={design} onChange={patchDoc} />
                  )}
                  {tab.value === "stamps" && design.document.stamps && (
                    <StampsPanel design={design} onChange={patchDoc} />
                  )}
                </div>
              </div>
            </div>

            {/* Footer */}
            <div class="border-t border-base-content/10 p-4 flex flex-col gap-3 bg-base-100">
              <DesignChecks design={design} />
              <div class="flex items-center justify-between gap-3 flex-wrap">
                {/* Which platforms this one design is issued for. */}
                <div class="flex items-center gap-1.5">
                  <span class="text-xs text-base-content/50">Targets</span>
                  {WALLET_PLATFORMS.map((p) => {
                    const on = design.document[p].enabled;
                    return (
                      <button
                        key={p}
                        class={`btn btn-xs gap-1 ${on ? "btn-primary" : "btn-ghost border border-base-content/15"}`}
                        onClick={() => togglePlatform(p, !on)}
                        title={`${PLATFORM_META[p].label}${on ? "" : " (off)"}`}
                      >
                        <span class="material-symbols-outlined text-sm">
                          {PLATFORM_META[p].icon}
                        </span>
                      </button>
                    );
                  })}
                  {design.program_count > 0 && (
                    <span class="text-xs text-base-content/50 ml-2">
                      Used by {design.program_count}{" "}
                      {design.program_count === 1 ? "program" : "programs"}.
                      Issued passes pick up changes on their next update.
                    </span>
                  )}
                </div>
                <div class="flex items-center gap-2">
                  <button class="btn btn-sm btn-ghost" onClick={props.onClose}>
                    Cancel
                  </button>
                  <button
                    class="btn btn-sm btn-primary"
                    disabled={props.saving || checks.value.errors.length > 0}
                    onClick={() => props.onSave(design)}
                  >
                    {props.saving && (
                      <span class="loading loading-spinner loading-xs" />
                    )}
                    {isUnsaved(design) ? "Create design" : "Save design"}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </RightSheet>
      );
    };
  },
});
